import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { AlertCircle, ArrowLeft } from "lucide-react";
import { Logo } from "@/components/brand/logo";

const errorMessages: Record<string, { title: string; description: string }> = {
	unable_to_create_user: {
		title: "Account already exists",
		description:
			"An account with this email already exists. Try signing in with your email and password, or use a different authentication method.",
	},
	email_not_verified: {
		title: "Email not verified",
		description:
			"Please verify your email address before signing in. Check your inbox for the verification link.",
	},
	invalid_credentials: {
		title: "Invalid credentials",
		description:
			"The email or password you entered is incorrect. Please try again.",
	},
	session_expired: {
		title: "Session expired",
		description: "Your session has expired. Please sign in again to continue.",
	},
	access_denied: {
		title: "Access denied",
		description: "You don't have permission to access this resource.",
	},
	default: {
		title: "Authentication error",
		description:
			"Something went wrong during authentication. Please try again.",
	},
};

export const Route = createFileRoute("/api/auth/error")({
	component: AuthErrorPage,
	validateSearch: (search: Record<string, unknown>) => ({
		error: (search.error as string) || "default",
	}),
});

function AuthErrorPage() {
	const { error } = useSearch({ from: "/api/auth/error" });
	const errorInfo = errorMessages[error] || errorMessages.default;

	return (
		<div className="min-h-svh flex flex-col bg-background">
			<div className="flex items-center justify-between px-6 py-4 lg:px-12">
				<Logo size="sm" />
				<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
					&gt; Auth / error
				</span>
			</div>

			<div className="flex-1 flex items-center justify-center px-6 py-12">
				<div className="w-full max-w-[420px]">
					<div className="border border-border bg-card p-6 sm:p-8">
						<div className="inline-flex size-10 items-center justify-center border border-[color:var(--ticker-down)]/40 bg-destructive/10">
							<AlertCircle className="size-4 text-[color:var(--ticker-down)]" />
						</div>

						<p className="mt-5 font-mono text-sm uppercase tracking-[0.14em] text-[color:var(--ticker-down)]">
							&gt; Error
						</p>
						<h1
							className="mt-1 font-mono text-xl leading-tight tracking-[-0.02em] text-foreground"
							style={{ fontWeight: 400 }}
						>
							{errorInfo.title}
						</h1>
						<p className="mt-2 text-base leading-[1.55] text-muted-foreground">
							{errorInfo.description}
						</p>

						{error !== "default" && (
							<div className="mt-4 border border-border bg-muted/30 px-3 py-2">
								<p className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
									Error code ·{" "}
									<span className="text-foreground normal-case tracking-normal">
										{error}
									</span>
								</p>
							</div>
						)}

						<div className="mt-6 space-y-2">
							<Link
								to="/login"
								className="inline-flex w-full min-h-11 items-center justify-center gap-2 bg-brand px-5 font-mono text-sm uppercase tracking-[0.14em] text-background transition-colors hover:bg-brand-hover"
							>
								Sign in
							</Link>
							<Link
								to="/signup"
								className="inline-flex w-full min-h-11 items-center justify-center gap-2 border border-border bg-background px-5 font-mono text-sm uppercase tracking-[0.14em] text-foreground transition-colors hover:border-brand/40 hover:text-brand"
							>
								Create account
							</Link>
						</div>

						<div className="mt-5 text-center">
							<Link
								to="/"
								className="inline-flex items-center gap-1.5 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-brand"
							>
								<ArrowLeft className="size-3" />
								Back to home
							</Link>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
